import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FaBell, FaUser, FaCog, FaSignOutAlt, FaMicrophone } from "react-icons/fa";
import Swal from "sweetalert2";
import { toast } from "react-toastify";

const PressHeader = () => {
  const navigate = useNavigate();
  const [userInfo, setUserInfo] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const pressUser = localStorage.getItem("pressUser");
    if (pressUser) {
      try { setUserInfo(JSON.parse(pressUser)); } catch { }
    }
  }, []);

  // close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = () => {
    setShowDropdown(false);
    Swal.fire({
      title: "Logout?",
      text: "Are you sure you want to end your session?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3b82f6",
      cancelButtonColor: "#ef4444",
      confirmButtonText: "Yes, logout",
      cancelButtonText: "Cancel",
      reverseButtons: true, // Standard mobile UX
      customClass: {
        popup: 'rounded-[2rem] p-4 sm:p-6',
        title: 'text-xl font-black text-slate-800',
        htmlContainer: 'text-sm font-medium text-slate-500',
        confirmButton: 'rounded-xl font-bold px-6 py-2.5',
        cancelButton: 'rounded-xl font-bold px-6 py-2.5 bg-slate-100 text-slate-600 hover:bg-slate-200 border-none'
      }
    }).then((result) => {
      if (result.isConfirmed) {
        localStorage.removeItem("pressToken");
        localStorage.removeItem("pressUser");
        localStorage.removeItem("userRole");
        navigate("/press/login");
        toast.success("Successfully logged out");
      }
    });
  };

  // generate initials from name
  const initials = userInfo?.name
    ? userInfo.name.trim().split(" ").slice(0, 2).map(w => w[0].toUpperCase()).join("")
    : "PU";

  return (
    <header className="bg-white border-b border-slate-100 shadow-sm px-3 sm:px-6 h-12 sm:h-14 flex items-center justify-between flex-shrink-0">

      {/* ── Left: Brand ── */}
      <div className="flex items-center gap-2">
        <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center flex-shrink-0">
          <FaMicrophone className="text-white w-3 h-3 sm:w-3.5 sm:h-3.5" />
        </div>
        <div className="hidden xs:block">
          <p className="text-sm font-black text-slate-800 leading-none">Press Conference</p>
          <p className="text-[9px] text-slate-400 font-bold uppercase tracking-widest leading-none mt-0.5">Media Dashboard</p>
        </div>
        <p className="xs:hidden text-xs font-black text-slate-800">Press</p>
      </div>

      {/* ── Right: Actions ── */}
      <div className="flex items-center gap-1.5 sm:gap-3">

        {/* Notification Bell */}
        <button className="relative w-8 h-8 flex items-center justify-center rounded-xl bg-slate-50 border border-slate-100 text-slate-500 hover:bg-slate-100 transition-all active:scale-95">
          <FaBell className="w-3.5 h-3.5" />
          <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full border border-white" />
        </button>

        {/* User Avatar + Dropdown */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setShowDropdown(!showDropdown)}
            className="flex items-center gap-2 h-8 pl-1 pr-2 sm:px-2 rounded-xl bg-slate-50 border border-slate-100 hover:bg-slate-100 transition-all active:scale-95"
          >
            {/* Avatar */}
            <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
              <span className="text-white text-[9px] font-black">{initials}</span>
            </div>
            {/* Name — hidden on very small screens */}
            <div className="hidden sm:block text-left">
              <p className="text-[11px] font-black text-slate-700 leading-none truncate max-w-[80px]">
                {userInfo?.name || "Press User"}
              </p>
              <p className="text-[9px] text-slate-400 leading-none mt-0.5 truncate max-w-[80px]">
                {userInfo?.organization || "Media"}
              </p>
            </div>
            {/* Chevron */}
            <svg className={`hidden sm:block w-2.5 h-2.5 text-slate-400 transition-transform duration-200 ${showDropdown ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {/* Dropdown */}
          {showDropdown && (
            <div className="absolute right-0 mt-2 w-52 bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden z-50 animate-in slide-in-from-top-2 duration-200">
              {/* User info header */}
              <div className="px-4 py-3 bg-gradient-to-br from-blue-600 to-indigo-700">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-white/20 border border-white/30 flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-sm font-black">{initials}</span>
                  </div>
                  <div className="min-w-0">
                    <p className="text-white text-xs font-black leading-tight truncate">{userInfo?.name || "Press User"}</p>
                    <p className="text-white/60 text-[10px] truncate mt-0.5">{userInfo?.email || ""}</p>
                    {userInfo?.pressConferenceId && (
                      <p className="text-blue-200 text-[9px] font-bold mt-0.5">ID: {userInfo.pressConferenceId}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Menu Items */}
              <div className="py-1.5">
                <button
                  onClick={() => { navigate("/press/profile"); setShowDropdown(false); }}
                  className="w-full text-left px-4 py-2.5 text-xs text-slate-700 hover:bg-slate-50 flex items-center gap-3 transition-colors font-semibold"
                >
                  <div className="w-6 h-6 rounded-lg bg-blue-50 flex items-center justify-center">
                    <FaUser className="w-3 h-3 text-blue-500" />
                  </div>
                  Profile
                </button>
                <button
                  onClick={() => { navigate("/press/settings"); setShowDropdown(false); }}
                  className="w-full text-left px-4 py-2.5 text-xs text-slate-700 hover:bg-slate-50 flex items-center gap-3 transition-colors font-semibold"
                >
                  <div className="w-6 h-6 rounded-lg bg-slate-50 flex items-center justify-center">
                    <FaCog className="w-3 h-3 text-slate-500" />
                  </div>
                  Settings
                </button>
              </div>

              {/* Logout */}
              <div className="border-t border-slate-100 p-2">
                <button
                  onClick={handleLogout}
                  className="w-full text-left px-3 py-2.5 text-xs text-rose-600 hover:bg-rose-50 flex items-center gap-3 rounded-xl transition-colors font-black"
                >
                  <div className="w-6 h-6 rounded-lg bg-rose-50 flex items-center justify-center">
                    <FaSignOutAlt className="w-3 h-3 text-rose-500" />
                  </div>
                  Logout
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default PressHeader;
